(() => {
  class CartTabs extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;
        this.drawer = this.closest('cart-drawer');
        this.content = this.drawer.querySelector('.drawer__content');
        this.footer = this.drawer.querySelector('.drawer__footer');
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.position = this.querySelector('.mini-cart__tab-position');
        this.panels = {
          cart: this.drawer.querySelector('#mini-cart-panel-cart'),
          recommendations: this.drawer.querySelector('#mini-cart-panel-recommendations')
        };
        if (!this.panels.cart || !this.panels.recommendations) return;
        this.media = matchMedia('(max-width: 999px)');
        this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        this.animations = new Set();
        this.transitionId = 0;
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.addEventListener('click', event => {
          const tab = event.target.closest('[role="tab"]');
          if (tab) this.select(tab.dataset.tab);
        }, options);
        this.addEventListener('keydown', event => {
          const tab = event.target.closest('[role="tab"]');
          if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const index = this.tabs.indexOf(tab);
          const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? this.tabs.length - 1 :
            (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[nextIndex].focus();
          this.select(this.tabs[nextIndex].dataset.tab);
        }, options);
        const resetMotion = () => {
          this.cancelMotion();
          this.render();
        };
        this.media.addEventListener('change', resetMotion, options);
        this.reducedMotion.addEventListener('change', resetMotion, options);
        this.drawer.addEventListener('variant:added', () => { this.returnToCart = true; }, options);
        this.openObserver = new MutationObserver(() => {
          if (this.drawer.hasAttribute('open')) this.select('cart', true, false);
          else {
            this.cancelMotion();
            this.render();
          }
        });
        this.openObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'] });
        document.documentElement.addEventListener('cart:refresh', event => {
          if (event.detail?.openMiniCart || this.returnToCart) {
            this.returnToCart = false;
            this.select('cart', true, false);
          }
        }, options);
        const recommendations = this.panels.recommendations.querySelector('cart-drawer-recommendations');
        this.recommendationsObserver = new MutationObserver(() => this.updateRecommendations());
        this.recommendationsObserver.observe(recommendations, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
        this.resizeObserver = new ResizeObserver(() => this.adjustPosition(false));
        this.resizeObserver.observe(this.querySelector('.mini-cart__tab-list'));
        this.drawer.dataset.mobileTabsReady = '';
        this.select(this.drawer.mobileCartTab || 'cart', false, false);
        this.updateRecommendations();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.openObserver?.disconnect();
      this.recommendationsObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.cancelMotion();
      cancelAnimationFrame(this.positionFrame);
      const drawer = this.drawer;
      queueMicrotask(() => {
        if (drawer && !drawer.querySelector('fabbricantti-cart-tabs')) {
          delete drawer.mobileCartTab;
          delete drawer.mobileCartScroll;
          delete drawer.dataset.mobileTabsReady;
          delete drawer.dataset.mobileTab;
        }
      });
    }

    async select(tab, saveScroll = true, animate = true) {
      if (animate && this.drawer.mobileCartTab === tab) return;
      const previous = this.visibleTab || this.drawer.mobileCartTab || 'cart';
      if (saveScroll && this.media.matches) {
        this.drawer.mobileCartScroll ||= { cart: 0, recommendations: 0 };
        this.drawer.mobileCartScroll[previous] = this.content.scrollTop;
      }
      this.cancelMotion();
      const transitionId = this.transitionId;
      this.drawer.mobileCartTab = tab;
      this.updateTabs(tab);
      const shouldAnimate = animate && this.canAnimate();
      this.adjustPosition(shouldAnimate);
      if (shouldAnimate && previous !== tab) {
        this.panels[previous].inert = true;
        if (this.footer) this.footer.inert = true;
        const outgoing = this.play(this.panels[previous], { opacity: [1, 0] }, { duration: 250, easing: 'ease' }, 'exit');
        if (this.footer) this.play(this.footer, { opacity: [1, 0] }, { duration: 250, easing: 'ease' }, 'footer-exit');
        await outgoing.finished.catch(() => {});
        if (transitionId !== this.transitionId || !this.isConnected) return;
      }
      this.render(tab, shouldAnimate);
      if (this.media.matches && saveScroll) this.content.scrollTop = this.drawer.mobileCartScroll?.[tab] || 0;
      if (shouldAnimate) this.animateContents(tab);
    }

    updateTabs(tab) {
      this.tabs.forEach(button => {
        const selected = button.dataset.tab === tab;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
    }

    render(tab = this.drawer.mobileCartTab || 'cart', animatePosition = false) {
      this.visibleTab = tab;
      this.drawer.dataset.mobileTab = this.media.matches ? tab : 'desktop';
      this.updateTabs(tab);
      if (this.footer) this.footer.inert = false;
      Object.entries(this.panels).forEach(([name, panel]) => {
        panel.hidden = this.media.matches && name !== tab;
        panel.inert = panel.hidden;
        if (this.media.matches) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', `mini-cart-tab-${name}`);
          panel.tabIndex = 0;
        } else {
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
          panel.removeAttribute('tabindex');
        }
      });
      this.adjustPosition(animatePosition);
    }

    adjustPosition(animate) {
      if (!this.position || !this.media.matches) return;
      const button = this.tabs.find(tab => tab.getAttribute('aria-selected') === 'true');
      const list = this.position.parentElement;
      if (!button || !list.clientWidth || !button.clientWidth) return;
      cancelAnimationFrame(this.positionFrame);
      if (!animate) this.position.classList.remove('is-initialized');
      const scale = button.clientWidth / list.clientWidth;
      this.position.style.setProperty('--scale', scale);
      this.position.style.setProperty('--translate', `${button.offsetLeft / button.clientWidth * 100}%`);
      if (!animate) {
        this.position.getBoundingClientRect();
        this.positionFrame = requestAnimationFrame(() => this.position.classList.add('is-initialized'));
      }
    }

    canAnimate() {
      return this.media.matches && !this.reducedMotion.matches && this.drawer.hasAttribute('open');
    }

    play(element, keyframes, timing, name) {
      const animation = element.animate(keyframes, { ...timing, fill: 'both' });
      animation.id = `cart-tabs-${name}`;
      this.animations.add(animation);
      animation.finished.then(() => {
        this.animations.delete(animation);
        animation.cancel();
      }, () => this.animations.delete(animation));
      return animation;
    }

    cancelMotion() {
      this.transitionId = (this.transitionId || 0) + 1;
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }

    animateContents(tab) {
      this.play(this.panels[tab], { opacity: [0, 1] }, { duration: 250, easing: 'ease' }, 'enter');
      if (tab === 'recommendations') {
        this.animateProducts([...this.panels.recommendations.querySelectorAll('.product-item')]);
      } else {
        [...this.panels.cart.querySelectorAll('.line-item')].forEach((item, index) => {
          this.play(item, { opacity: [0, 1], transform: ['translateX(40px)', 'translateX(0)'] }, {
            duration: 400,
            delay: 120 * index - Math.min(2 * index * index, 120 * index),
            easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
          }, 'cart-item');
        });
      }
      if (this.footer) this.play(this.footer, { opacity: [0, 1], transform: ['translateY(100%)', 'translateY(0)'] }, {
        duration: 300,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)'
      }, 'footer');
    }

    animateProducts(items) {
      items.forEach((item, index) => {
        this.play(item, { opacity: [0, 1], transform: ['translateY(35px)', 'translateY(0)'] }, {
          duration: 600,
          delay: 100 * index - Math.min(3 * index * index, 100 * index),
          easing: 'ease'
        }, 'product');
      });
    }

    updateRecommendations() {
      const panel = this.panels.recommendations;
      const recommendations = panel.querySelector('cart-drawer-recommendations');
      panel.querySelector('.mini-cart__recommendations-empty').hidden = !recommendations.hidden && !!recommendations.querySelector('.product-item, .spinner');
      const items = [...recommendations.querySelectorAll('.product-item')];
      const newItems = items.filter(item => !this.recommendedItems?.has(item));
      this.recommendedItems = new Set(items);
      if (newItems.length && this.visibleTab === 'recommendations' && this.drawer.mobileCartTab === 'recommendations' && this.canAnimate()) {
        this.animateProducts(newItems);
      }
    }
  }

  customElements.define('fabbricantti-cart-tabs', CartTabs);
})();
